import { getWorkDate } from "@components/inputs/date/utils";
import { getDocumentTypeCodeFromEntityType, getEntityTypeFromBindingContext } from "@odata/EntityTypes";
import { _handleAccAssignmentChange, handleAccAssignmentChildrenChange } from "@pages/accountAssignment/AccountAssignment.utils";
import { getDefaultPercetageFromImpactCode } from "@pages/cashBasisAccounting/CashBasisAccounting.utils";
import {
    ACCOUNTING_BULK_CHANGE_FIELD_DEFS,
    BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS,
    BulkChangeDateSource,
    CBA_BULK_CHANGE_FIELD_DEFS,
    getBulkChangeDefsForDocumentType,
    IBulkChangeContext,
    IBulkChangeFieldDef,
    IBulkChangeGroup,
    IProformaTableCustomData
} from "@pages/documents/BulkChange.utils";
import { ProformaType } from "@pages/documents/proformaInvoices/ProformaInvoice.global.utils";
import { getDefByEntityType } from "@pages/getDefByEntityType";
import { TFieldDefinition, TFieldsDefinition } from "@pages/PageUtils";
import { isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { isDefined, isEmptyArray } from "@utils/general";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { NEW_ITEM_DETAIL } from "../../../constants";
import { FieldType, IconSize, QueryParam, TableViewMode, TextAlign, ValidationErrorType } from "../../../enums";
import { TRecordAny, TRecordType } from "../../../global.types";
import { TableStorage } from "../../../model/TableStorage";
import { IValidationError } from "../../../model/Validator.types";
import BindingContext, { IEntity } from "../../../odata/BindingContext";
import { DocumentCbaCategoryEntity, DocumentEntity, IDocumentLabelEntity } from "../../../odata/GeneratedEntityTypes";
import { CbaCategoryTaxImpactCode, DocumentTypeCode, SelectionCode } from "../../../odata/GeneratedEnums";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";
import { Button } from "../../button";
import Dialog from "../../dialog/Dialog";
import { ArrowIcon } from "../../icon";
import SimpleTable from "../../simpleTable/SimpleTable";
import { IRow } from "../../table";
import { IFieldDef } from "../FieldInfo";
import { ISmartFieldBlur, ISmartFieldChange, ISmartFieldTempDataActionArgs } from "../smartField/SmartField";
import { IFormGroupDef } from "../smartFormGroup/SmartFormGroup";
import { Header } from "./BulkChange.styles";
import { BulkChangeForm } from "./BulkChangeForm";
import {
    BulkChangeAction,
    getBulkChangeActionTranslation,
    getDefaultBulkChangeAction,
    IBulkChangeDialogStorageCustomData
} from "./SmartBulkChange.utils";

export type TBulkConfirmData = { data: IEntity, bulkActions: TRecordType<BulkChangeAction> };

const DDOP_PROFORMA_TYPE = ProformaType.DDOP;

type TBulkChangeFieldDefinition = TFieldDefinition & {
    bulkChangeAdditionalFields?: IFieldDef[];
};

interface IProps extends WithTranslation {
    onConfirm: (args: TBulkConfirmData) => void;
    onCancel: () => void;
    bindingContext: BindingContext;
    tableStorage: TableStorage;
}


interface IState {
    isConfirmationPage: boolean;
    storage?: FormStorage<IEntity, IBulkChangeDialogStorageCustomData>;
}

function addCollapsedSubFieldDefs(
    fieldDefinition: TFieldsDefinition,
    massFieldDef: TFieldDefinition,
    originalFieldDef: TFieldsDefinition
): void {
    for (const row of massFieldDef.collapsedRows) {
        for (const fieldRef of row) {
            if (fieldRef.id && !fieldDefinition[fieldRef.id]) {
                const originalDef = originalFieldDef[fieldRef.id];
                fieldDefinition[fieldRef.id] = {
                    ...originalDef,
                    ...fieldRef,
                    fieldSettings: {
                        ...originalDef?.fieldSettings,
                        ...fieldRef.fieldSettings
                    }
                };
            }
        }
    }
}

function addBulkChangeAdditionalFieldDefs(
    fieldDefinition: TFieldsDefinition,
    massFieldDef: TFieldDefinition,
    originalFieldDef: TFieldsDefinition
): void {
    for (const fieldRef of (massFieldDef as TBulkChangeFieldDefinition).bulkChangeAdditionalFields ?? []) {
        if (fieldRef.id && !fieldDefinition[fieldRef.id]) {
            const originalDef = originalFieldDef[fieldRef.id];
            fieldDefinition[fieldRef.id] = {
                ...originalDef,
                ...fieldRef,
                fieldSettings: {
                    ...originalDef?.fieldSettings,
                    ...fieldRef.fieldSettings
                }
            };
        }
    }
}

function getAllowedBulkChangeActions(
    path: string,
    fieldDef: TFieldDefinition,
    originalFieldDef: TFieldDefinition,
    isDraftBulkChange: boolean
): BulkChangeAction[] {
    const isDateField = originalFieldDef?.type === FieldType.Date || path.startsWith("Date");

    if (isDateField) {
        return isDraftBulkChange
            ? [BulkChangeAction.SetForEmptyValues, BulkChangeAction.Replace, BulkChangeAction.RemoveExact, BulkChangeAction.RemoveAll]
            : [BulkChangeAction.Replace];
    }

    if (path === "Explanation") {
        return isDraftBulkChange
            ? [BulkChangeAction.Append, BulkChangeAction.Prepend, BulkChangeAction.Replace, BulkChangeAction.RemoveAll]
            : [BulkChangeAction.Append, BulkChangeAction.Prepend, BulkChangeAction.Replace];
    }

    if (path === "AccountAssignmentSelection/AccountAssignment") {
        return isDraftBulkChange
            ? [BulkChangeAction.SetForEmptyValues, BulkChangeAction.Replace, BulkChangeAction.RemoveExact, BulkChangeAction.RemoveAll]
            : [BulkChangeAction.SetForEmptyValues, BulkChangeAction.Replace];
    }

    if (path === "VatClassificationSelection/VatClassification") {
        return isDraftBulkChange
            ? [BulkChangeAction.SetForEmptyValues, BulkChangeAction.Replace]
            : [BulkChangeAction.Replace];
    }

    return fieldDef.allowedBulkChangeActions as BulkChangeAction[];
}

function getIsRequired(path: string): boolean {
    if (path === "CbaCategory/Category") {
        return false;
    }

    if (path in BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS) {
        return true;
    }

    return true;
}

function buildBulkChangeFieldDefinition(
    availableDefs: string[],
    bulkChangeDefs: IBulkChangeFieldDef[],
    massEditableDef: TFieldsDefinition,
    originalFieldDef: TFieldsDefinition,
    isDraftBulkChange: boolean
): TFieldsDefinition {
    const fieldDefinition: TFieldsDefinition = {};
    availableDefs.forEach(key => {
        const massFieldDef = massEditableDef[key];
        const originalDef = originalFieldDef[key];
        const bulkChangeDef = bulkChangeDefs.find(def => def.changeType === key);
        fieldDefinition[key] = {
            ...originalDef,
            ...massFieldDef,
            fieldSettings: {
                ...originalDef?.fieldSettings,
                ...massFieldDef.fieldSettings
            },
            ...(bulkChangeDef?.defaultValue ? {
                defaultValue: bulkChangeDef.defaultValue
            } : {}),
            isRequired: getIsRequired(key),
            allowedBulkChangeActions: getAllowedBulkChangeActions(key, massFieldDef, originalDef, isDraftBulkChange)
        };
        if (massFieldDef?.noCollapsedExpansion && massFieldDef?.collapsedRows) {
            addCollapsedSubFieldDefs(fieldDefinition, massFieldDef, originalFieldDef);
        }
        addBulkChangeAdditionalFieldDefs(fieldDefinition, massFieldDef, originalFieldDef);
    });
    return fieldDefinition;
}

class BulkChangeDialog extends React.Component<IProps, IState> {
    _formRef = React.createRef<any>();

    state: IState = {
        isConfirmationPage: false
    };

    constructor(props: IProps) {
        super(props);

        this.state.storage = this.createFormStorage();
        // init as new (empty) form storage
        this.state.storage.init({
            bindingContext: this.props.tableStorage.data.bindingContext.addKey(NEW_ITEM_DETAIL, true),
            data: this.isTaxProformaBulkChange ? { IsTaxDocument: true } : {}, // just empty form without default values
            ignoreVariants: true
        })
            .then(() => this.forceUpdate());
    }

    get entityType() {
        return getEntityTypeFromBindingContext(this.props.tableStorage.data.bindingContext);
    }

    get bulkChangeDocumentType(): DocumentTypeCode {
        return getDocumentTypeCodeFromEntityType(this.entityType);
    }

    get isTaxProformaBulkChange(): boolean {
        const proformaDocumentTypes = [DocumentTypeCode.ProformaInvoiceReceived, DocumentTypeCode.ProformaInvoiceIssued];
        const customData = this.props.tableStorage.getCustomData<IProformaTableCustomData>();

        return proformaDocumentTypes.includes(this.bulkChangeDocumentType)
            && (customData.isTaxProforma ?? getQueryParameters()[QueryParam.Type] === DDOP_PROFORMA_TYPE);
    }

    get bulkChangeContext(): IBulkChangeContext {
        return {
            documentTypeCode: this.bulkChangeDocumentType,
            isTaxProformaDocument: this.isTaxProformaBulkChange
        };
    }

    get confirmText() {
        const num = this.props.tableStorage.getCustomData().affectedRows?.length;
        return this.props.t(`Common:General.${this.state.isConfirmationPage ? "FinishWithCount" : "Continue"}`, { count: num });
    }

    get dialogTitle() {
        const { t, tableStorage } = this.props;
        const isProforma = [DocumentTypeCode.ProformaInvoiceReceived, DocumentTypeCode.ProformaInvoiceIssued].includes(this.bulkChangeDocumentType);
        const type = isProforma
            ? t(`${this.bulkChangeDocumentType === DocumentTypeCode.ProformaInvoiceReceived ? "ProformaInvoicesReceived" : "ProformaInvoicesIssued"}:${this.isTaxProformaBulkChange ? "DDOP" : "Proforma"}_TitlePlural`)
            : tableStorage.data.definition.title;

        return t("Common:BulkChange.Title", { type });
    }

    get dialogHeader() {
        return this.props.t(`Common:BulkChange.${this.state.isConfirmationPage ? "ConfirmationHeader" : "Header"}`);
    }

    buildBulkChangeGroups = (bulkChangeDefs: IBulkChangeFieldDef[], bulkChangeDef: TFieldsDefinition): IFormGroupDef[] => {
        const groupOrder: IBulkChangeGroup[] = [];
        const rowsByGroup = new Map<string, IFieldDef[]>();

        bulkChangeDefs.forEach(def => {
            if (!bulkChangeDef[def.changeType]) {
                return;
            }
            if (!rowsByGroup.has(def.group.id)) {
                rowsByGroup.set(def.group.id, []);
                groupOrder.push(def.group);
            }
            rowsByGroup.get(def.group.id).push({ id: def.changeType });
        });

        return groupOrder.map(group => ({
            id: group.id,
            title: this.props.t(group.translationKey),
            rows: [rowsByGroup.get(group.id)]
        }));
    };

    createFormStorage = () => {
        const definition = getDefByEntityType(this.entityType)(this.props.tableStorage.context);
        // create form definition from massEditable fields' def merged with form group definitions
        const formDef = definition.form;
        const isCBA = isCashBasisAccountingCompany(this.props.tableStorage.context);
        const allBulkChangeDefs = isCBA ? CBA_BULK_CHANGE_FIELD_DEFS : ACCOUNTING_BULK_CHANGE_FIELD_DEFS;
        const bulkChangeDefs = getBulkChangeDefsForDocumentType(allBulkChangeDefs, this.bulkChangeContext);
        const allowedChangeTypes = new Set(bulkChangeDefs.map(def => def.changeType));
        const availableDefs = Object.keys(definition.table.massEditableDef).filter(key => allowedChangeTypes.has(key));
        formDef.fieldDefinition = buildBulkChangeFieldDefinition(
            availableDefs,
            bulkChangeDefs,
            definition.table.massEditableDef,
            definition.form.fieldDefinition,
            this.props.tableStorage.data.tableViewMode === TableViewMode.Draft
        );

        // todo: DEV-21949 (end)
        formDef.groups = this.buildBulkChangeGroups(bulkChangeDefs, formDef.fieldDefinition);


        const formStorage = new FormStorage({
            oData: this.props.tableStorage.oData,
            t: this.props.t,
            context: this.props.tableStorage.context,
            id: formDef.id,
            definition: formDef,
            refresh: (refreshPage) => {
                this._formRef.current?.forceUpdate();
                if (refreshPage) {
                    this.forceUpdate();
                }
            }
        }) as FormStorage<IEntity, IBulkChangeDialogStorageCustomData>;

        formStorage.setCustomData({
            isChecked: {}
        });

        return formStorage;
    };

    getCheckedRows = () => {
        return this.state.storage.getCustomData().isChecked;
    };

    /** Validates all fields that has been selected to be used by the user */
    async validateSelected(): Promise<boolean> {
        const { storage } = this.state;
        const promises: Array<Promise<IValidationError>> = [];
        // Go through all values that use has selected, checks if value is set
        const checkedRows = this.getCheckedRows();

        for (const [path, isChecked] of Object.entries(checkedRows)) {
            const bindingContext = this.state.storage.data.bindingContext.navigate(path);
            const action = this.state.storage.getAdditionalFieldData(bindingContext, "parsedValue") as BulkChangeAction;
            const isCollection = bindingContext.isCollection();

            if (!isChecked || action === BulkChangeAction.RemoveAll || (action === BulkChangeAction.Remove && !isCollection)) {
                continue;
            }

            if (isCollection) {
                const value = storage.getValue(bindingContext);

                if (isEmptyArray(value)) {
                    const error = {
                        errorType: ValidationErrorType.Field,
                        message: this.state.storage.t("Validation.Required")
                    };
                    promises.push(Promise.resolve(error));
                    storage.setError(bindingContext, error);
                }

                continue;
            }

            if (storage.data.definition.fieldDefinition[path]) {
                promises.push(storage.validateField(bindingContext));
            }

            if (path === "AccountAssignmentSelection/AccountAssignment") {
                const selectionCodeBc = storage.data.bindingContext.navigate("AccountAssignmentSelection/Selection/Code");
                const selectionCode = storage.getValue(selectionCodeBc) as SelectionCode;
                const assignmentBc = storage.data.bindingContext.navigate("AccountAssignmentSelection/AccountAssignment");
                const debitBc = assignmentBc.navigate("DebitAccount");
                const creditBc = assignmentBc.navigate("CreditAccount");
                const debitValue = storage.getValue(debitBc, { useDirectValue: false });
                const creditValue = storage.getValue(creditBc, { useDirectValue: false });

                if (selectionCode === SelectionCode.Own || debitValue || creditValue) {
                    if (!debitValue) {
                        const err: IValidationError = {
                            errorType: ValidationErrorType.Field,
                            message: storage.t("Validation.Required")
                        };
                        storage.setError(debitBc, err);
                        promises.push(Promise.resolve(err));
                    }
                    if (!creditValue) {
                        const err: IValidationError = {
                            errorType: ValidationErrorType.Field,
                            message: storage.t("Validation.Required")
                        };
                        storage.setError(creditBc, err);
                        promises.push(Promise.resolve(err));
                    }
                }
            }

            if (path === "CbaCategory/TaxImpact") {
                const { taxPercentagePath } = this.getCbaCategoryPaths();
                const taxImpactCode = storage.getValue(bindingContext) as CbaCategoryTaxImpactCode;
                const taxPercentageBc = storage.data.bindingContext.navigate(taxPercentagePath);
                const taxPercentage = storage.getValue(taxPercentageBc);

                if (taxImpactCode === CbaCategoryTaxImpactCode.Partial && !isDefined(taxPercentage)) {
                    const err: IValidationError = {
                        errorType: ValidationErrorType.Field,
                        message: storage.t("Validation.Required")
                    };
                    storage.setError(taxPercentageBc, err);
                    promises.push(Promise.resolve(err));
                }
            }

            if (
                path in BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS &&
                this.props.tableStorage.data.tableViewMode !== TableViewMode.Draft &&
                storage.getValue(bindingContext) === BulkChangeDateSource.Own
            ) {
                const ownDatePath = BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS[path as keyof typeof BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS];
                const ownDateBc = storage.data.bindingContext.navigate(ownDatePath);
                const ownDate = storage.getValue(ownDateBc);

                if (!ownDate) {
                    const err: IValidationError = {
                        errorType: ValidationErrorType.Field,
                        message: storage.t("Validation.Required")
                    };
                    storage.setError(ownDateBc, err);
                    promises.push(Promise.resolve(err));
                }
            }
        }

        const results = await Promise.all(promises);

        return !results.find(error => isDefined(error));
    }

    handleConfirm = async () => {
        if (this.isConfirmDisabled) {
            return;
        }

        const { isConfirmationPage, storage } = this.state;

        if (isConfirmationPage) {
            const { entity, bindingContext } = storage.data;
            const data = storage.injectTemporaryGuids(storage.clearHiddenFields(entity));
            const bulkActions: TRecordType<BulkChangeAction> = {};
            const checkedRows = this.getCheckedRows();

            // Probably iterating only over checked rows is better since we only need the checked ones
            Object.entries(checkedRows)
                .forEach(([checkedPath, isChecked]) => {
                    if (!isChecked) {
                        return;
                    }
                    const bc = bindingContext.navigate(checkedPath);
                    const additionalData = storage.getAdditionalFieldData(bc, "parsedValue") as BulkChangeAction;
                    bulkActions[checkedPath] = additionalData ?? getDefaultBulkChangeAction(bc.isCollection());
                });


            this.props.onConfirm({ data, bulkActions });
        } else {
            const isValid = await this.validateSelected();

            if (isValid) {
                this.setState({ isConfirmationPage: true });
            } else {
                // displays validation errors in fields
                this.forceUpdate();
            }
        }
    };

    handleCancel = () => {
        this.props.onCancel();
    };

    handleBack = () => {
        if (this.state.isConfirmationPage) {
            this.setState({ isConfirmationPage: false });
        }
    };

    renderConfirmationButtons(isDisabled: boolean) {
        return (
            <>
                {this.state.isConfirmationPage &&
                    <Button icon={<ArrowIcon width={IconSize.S}/>} onClick={this.handleBack}
                            isTransparent>
                        {this.props.t("Common:General.Back")}
                    </Button>
                }

                <ConfirmationButtons confirmText={this.confirmText}
                                     onConfirm={this.handleConfirm}
                                     onCancel={this.handleCancel}
                                     isDisabled={isDisabled}/>
            </>
        );
    }

    handleBlur = (args: ISmartFieldBlur) => {
        this.state.storage.handleBlur(args)
            .then(() => this.state.storage.refreshFields());
    };

    handleAccountAssignmentChange = (e: ISmartFieldChange): void => {
        if (!e.triggerAdditionalTasks) {
            return;
        }
        const path = e.bindingContext.getNavigationPath(true);
        if (path === "AccountAssignmentSelection/AccountAssignment") {
            _handleAccAssignmentChange(this.state.storage, e.bindingContext, e.value);
        } else if (path.startsWith("AccountAssignmentSelection/AccountAssignment/")) {
            handleAccAssignmentChildrenChange(this.state.storage, e);
        }
    };

    getCbaCategoryPaths = (): Record<string, string> => {
        const cbaCategoryPath = DocumentEntity.CbaCategory;
        return {
            cbaCategoryPath,
            taxImpactPath: `${cbaCategoryPath}/${DocumentCbaCategoryEntity.TaxImpact}`,
            taxImpactCodePath: `${cbaCategoryPath}/${DocumentCbaCategoryEntity.TaxImpactCode}`,
            taxPercentagePath: `${cbaCategoryPath}/${DocumentCbaCategoryEntity.TaxPercentage}`,
            isAssetAcquisitionPath: `${cbaCategoryPath}/${DocumentCbaCategoryEntity.IsAssetAcquisition}`
        };
    };

    handleCbaCategoryChange = (e: ISmartFieldChange): void => {
        if (!e.triggerAdditionalTasks || !isCashBasisAccountingCompany(this.props.tableStorage.context)) {
            return;
        }

        const path = e.bindingContext.getNavigationPath(true);
        const {
            taxImpactPath,
            taxImpactCodePath,
            taxPercentagePath,
            isAssetAcquisitionPath
        } = this.getCbaCategoryPaths();

        if (path === taxImpactPath) {
            const taxImpactCode = e.value as CbaCategoryTaxImpactCode;

            this.state.storage.setValueByPath(taxImpactCodePath, taxImpactCode);
            this.state.storage.setValueByPath(taxPercentagePath, getDefaultPercetageFromImpactCode(taxImpactCode));
            this.state.storage.setValueByPath(isAssetAcquisitionPath, false);
        }
    };

    handleDateSourceChange = (e: ISmartFieldChange): void => {
        const path = e.bindingContext.getPath();

        if (!(path in BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS) || e.value !== BulkChangeDateSource.Own) {
            return;
        }

        const ownDatePath = BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS[path as keyof typeof BULK_CHANGE_DATE_SOURCE_OWN_DATE_PATHS];
        const ownDateBc = this.state.storage.data.bindingContext.navigate(ownDatePath);

        if (!this.state.storage.getValue(ownDateBc)) {
            this.state.storage.setValue(ownDateBc, getWorkDate());
        }
    };

    handleLabelChange = (e: ISmartFieldChange): void => {
        const path = e.bindingContext.getNavigationPath(true);

        if (e.triggerAdditionalTasks && path === "Labels") {
            const labels = this.state.storage.getValue(e.bindingContext) as IDocumentLabelEntity[];

            const update = labels?.map((label: IDocumentLabelEntity) => {
                const selectedLabel = e.selectedItems.find(item => item.id === label.Label.Id);
                return {
                    ...label,
                    Label:  {
                        ...label?.Label,
                        Name: selectedLabel?.additionalData?.Name
                    }
                };
            });

            this.state.storage.setValue(e.bindingContext, update);
        }
    }

    handleChange = (e: ISmartFieldChange) => {
        this.handleAccountAssignmentChange(e);
        this.state.storage.handleChange(e);
        this.handleLabelChange(e);
        this.handleDateSourceChange(e);
        this.handleCbaCategoryChange(e);
        this.state.storage.refreshFields();
    };

    handleTemporalChange = (e: ISmartFieldChange) => {
        this.state.storage.handleTemporalChange(e);
        this.state.storage.refreshFields();
    };

    handleFieldConfirm = async (args: ISmartFieldTempDataActionArgs) => {
        await this.state.storage.handleConfirm(args, this.handleChange);
        await this.handleFieldCancel(args);
    };

    handleFieldCancel = async (args: ISmartFieldTempDataActionArgs) => {
        await this.state.storage.handleCancel(args);
        this.state.storage.refreshFields();
    };

    get columns() {
        const { t } = this.props;
        return [
            { id: "label", label: t("Common:BulkChange.Label"), textAlign: TextAlign.Left },
            { id: "value", label: t("Common:BulkChange.Value"), textAlign: TextAlign.Left }
        ];
    }

    get rows(): IRow[] {
        const { storage } = this.state;
        const rows: IRow[] = [];
        const checkedRows = this.getCheckedRows();

        for (const path of Object.keys(storage.data.definition.fieldDefinition)) {
            if (!checkedRows[path]) {
                continue;
            }

            const bindingContext = this.state.storage.data.bindingContext.navigate(path);
            const info = storage.getInfo(bindingContext);

            if (info && storage.data.definition.fieldDefinition[info.id]) {
                const origDisplayValue = storage.getDisplayValue(info);
                const isCollection = bindingContext.isCollection();

                // Labels, etc...
                const parsedValue = storage.getAdditionalFieldData(bindingContext, "parsedValue") as string ?? getDefaultBulkChangeAction(isCollection);
                let displayValue = getBulkChangeActionTranslation(parsedValue as BulkChangeAction, this.props.t);

                if (parsedValue !== BulkChangeAction.RemoveAll && origDisplayValue) {
                    displayValue = `${displayValue}: ${origDisplayValue}`;
                }

                rows.push({
                    id: bindingContext.getFullPath(),
                    values: {
                        label: info?.label,
                        value: displayValue
                    }
                });
            }
        }

        return rows;
    }

    renderContent(rows: IRow[]) {
        return (<>
            <Header>
                {this.dialogHeader}
            </Header>
            {this.state.isConfirmationPage ? (
                <SimpleTable rows={rows} columns={this.columns}/>
            ) : (
                <BulkChangeForm ref={this._formRef}
                                storage={this.state.storage}
                                onBlur={this.handleBlur}
                                onCancel={this.handleFieldCancel}
                                onConfirm={this.handleFieldConfirm}
                                onChange={this.handleChange}
                                onTemporalChange={this.handleTemporalChange}
                />
            )}
        </>);
    }

    get isConfirmDisabled() {
        const checkedRows = Object.values(this.getCheckedRows());
        const isAnyChecked = checkedRows?.length > 0 && checkedRows.some(v => v);
        return (this.state.isConfirmationPage && !this.rows.length) || !isAnyChecked;
    }

    render() {
        const isLoaded = this.state.storage.loaded && this.props.tReady;
        const rows = this.state.isConfirmationPage ? this.rows : [];

        return (
            <Dialog title={this.dialogTitle}
                    onConfirm={this.handleConfirm}
                    onClose={this.handleCancel}
                    minWidth={"695px"}
                    busy={!isLoaded}
                    footer={this.renderConfirmationButtons(this.isConfirmDisabled)}
            >
                {isLoaded && this.renderContent(rows)}
            </Dialog>
        );
    }
}

export default withTranslation(["Common"])(BulkChangeDialog);
